import React, { useEffect, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';

import { CorporateCard, CorporateCardPayment, ServicePaymentStatus } from '../../types';
import { fetchAvailableCards, registerPayment } from '../../services';
import { formatCard } from '../../constants';

interface FormValues {
	cardId: number | '';
	amountUsd: string;
	paymentCurrency: string;
	paymentAmount: string;
	paidAt: string;
	note: string;
}

interface DuplicateInfo {
	existingPayments: CorporateCardPayment[];
}

interface Props {
	open: boolean;
	onClose: () => void;
	serviceId: string;
	saleOrderId: string;
	serviceCostUsd: number;
	providerName?: string;
	onSuccess: () => void;
	currentStatus: ServicePaymentStatus | null;
}

const toLocalDateTimeValue = (iso: string): string => {
	const d = new Date(iso);
	const pad = (n: number) => String(n).padStart(2, '0');
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const RegisterPaymentModal = ({
	open,
	onClose,
	serviceId,
	saleOrderId,
	serviceCostUsd,
	providerName,
	onSuccess,
	currentStatus,
}: Props): JSX.Element => {
	const [availableCards, setAvailableCards] = useState<CorporateCard[]>([]);
	const [cardsLoading, setCardsLoading] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [apiError, setApiError] = useState<string | null>(null);
	const [duplicate, setDuplicate] = useState<DuplicateInfo | null>(null);
	const [pendingBody, setPendingBody] = useState<Record<string, unknown> | null>(null);

	const {
		control,
		register,
		handleSubmit,
		reset,
		setValue,
		formState: { errors },
	} = useForm<FormValues>({
		defaultValues: {
			cardId: '',
			amountUsd: String(serviceCostUsd ?? ''),
			paymentCurrency: '',
			paymentAmount: '',
			paidAt: toLocalDateTimeValue(new Date().toISOString()),
			note: '',
		},
	});

	// Fetch de tarjetas al abrir el modal + sincronizar monto con el costo actual
	useEffect(() => {
		if (!open) return;

		if (serviceCostUsd > 0) {
			setValue('amountUsd', String(serviceCostUsd));
		}

		setCardsLoading(true);
		setAvailableCards([]);
		fetchAvailableCards()
			.then((cards) => {
				setAvailableCards(cards);
				if (cards.length > 0) {
					setValue('cardId', cards[0].id);
				}
			})
			.catch(() => setAvailableCards([]))
			.finally(() => setCardsLoading(false));
	}, [open, serviceCostUsd, setValue]);

	const handleClose = () => {
		reset();
		setApiError(null);
		setDuplicate(null);
		setPendingBody(null);
		onClose();
	};

	const buildBody = (values: FormValues, confirmDuplicate?: boolean) => {
		const body: Record<string, unknown> = {
			cardId: Number(values.cardId),
			serviceId,
			saleOrderId,
			amountUsd: parseFloat(values.amountUsd),
			paidAt: new Date(values.paidAt).toISOString(),
		};
		if (values.note.trim()) body.note = values.note.trim();
		if (values.paymentCurrency.trim() && values.paymentAmount.trim()) {
			body.paymentCurrency = values.paymentCurrency.trim().toUpperCase();
			body.paymentAmount = parseFloat(values.paymentAmount);
		}
		if (confirmDuplicate) body.confirmDuplicate = true;
		return body;
	};

	const submit = async (values: FormValues) => {
		setSubmitting(true);
		setApiError(null);
		const body = buildBody(values);

		try {
			await registerPayment(body as Parameters<typeof registerPayment>[0]);
			handleClose();
			onSuccess();
		} catch (err: unknown) {
			const axiosErr = err as {
				response?: { status?: number; data?: { existingPayments?: CorporateCardPayment[]; message?: string } };
			};
			if (axiosErr.response?.status === 409) {
				setDuplicate({ existingPayments: axiosErr.response.data?.existingPayments ?? [] });
				setPendingBody(buildBody(values));
			} else {
				setApiError(axiosErr.response?.data?.message ?? 'Ocurrió un error al registrar el pago.');
			}
		} finally {
			setSubmitting(false);
		}
	};

	const handleConfirmDuplicate = async () => {
		if (!pendingBody) return;
		setSubmitting(true);
		setApiError(null);
		try {
			await registerPayment({ ...pendingBody, confirmDuplicate: true } as Parameters<typeof registerPayment>[0]);
			handleClose();
			onSuccess();
		} catch (err: unknown) {
			const axiosErr = err as { response?: { data?: { message?: string } } };
			setApiError(axiosErr.response?.data?.message ?? 'Error al confirmar el pago.');
		} finally {
			setSubmitting(false);
		}
	};

	const hasPreviousActive = currentStatus && currentStatus.paid;

	const renderContent = () => {
		if (cardsLoading) {
			return (
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
					<CircularProgress size={24} />
				</Box>
			);
		}

		if (availableCards.length === 0) {
			return (
				<Alert severity="warning">
					No tenés tarjetas corporativas asignadas. Solicitá al administrador que te asigne una tarjeta.
				</Alert>
			);
		}

		return (
			<Box
				component="form"
				id="register-payment-form"
				onSubmit={handleSubmit(submit)}
				sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
			>
				{hasPreviousActive && !duplicate && (
					<Alert severity="info" sx={{ fontSize: '0.8rem' }}>
						Este servicio ya tiene pagos activos registrados. Podés agregar un pago adicional (parcial/complementario).
					</Alert>
				)}

				{duplicate && (
					<Alert severity="warning" sx={{ fontSize: '0.8rem' }}>
						<Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
							Este servicio ya tiene {duplicate.existingPayments.length > 1 ? 'pagos' : 'un pago'} activo registrado:
						</Typography>
						{duplicate.existingPayments.map((p) => (
							<Typography key={p.id} variant="body2">
								• USD {p.amountUsd.toFixed(2)} con {p.card.brand} ••••{p.card.last4} por {p.registeredBy} el{' '}
								{new Date(p.paidAt).toLocaleDateString('es-AR')}
							</Typography>
						))}
						<Typography variant="body2" sx={{ mt: 1 }}>
							¿Registrar otro pago adicional de todas formas?
						</Typography>
						<Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
							<Button size="small" variant="outlined" onClick={() => setDuplicate(null)}>
								Cancelar
							</Button>
							<Button
								size="small"
								variant="contained"
								color="warning"
								onClick={handleConfirmDuplicate}
								disabled={submitting}
							>
								{submitting ? <CircularProgress size={16} /> : 'Registrar de todas formas'}
							</Button>
						</Box>
					</Alert>
				)}

				{!duplicate && (
					<>
						<Controller
							name="cardId"
							control={control}
							rules={{ required: 'Seleccioná una tarjeta' }}
							render={({ field }) => (
								<FormControl fullWidth size="small" error={!!errors.cardId}>
									<InputLabel>Tarjeta</InputLabel>
									<Select label="Tarjeta" {...field}>
										{availableCards.map((card) => (
											<MenuItem key={card.id} value={card.id}>
												{formatCard(card)}
											</MenuItem>
										))}
									</Select>
									{errors.cardId && (
										<Typography variant="caption" color="error">
											{errors.cardId.message}
										</Typography>
									)}
								</FormControl>
							)}
						/>

						<TextField
							label="Monto USD"
							size="small"
							type="number"
							inputProps={{ step: '0.01', min: '0.01' }}
							error={!!errors.amountUsd}
							helperText={errors.amountUsd?.message}
							{...register('amountUsd', {
								required: 'El monto es requerido',
								min: { value: 0.01, message: 'Debe ser mayor a 0' },
							})}
						/>

						<Box sx={{ display: 'flex', gap: 2 }}>
							<TextField
								label="Moneda del pago real (opcional)"
								size="small"
								placeholder="EUR, GBP..."
								inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }}
								{...register('paymentCurrency')}
								sx={{ flex: 1 }}
							/>
							<TextField
								label="Monto en moneda real (opcional)"
								size="small"
								type="number"
								inputProps={{ step: '0.01', min: '0' }}
								{...register('paymentAmount')}
								sx={{ flex: 1 }}
							/>
						</Box>

						<TextField
							label="Fecha y hora del pago"
							size="small"
							type="datetime-local"
							InputLabelProps={{ shrink: true }}
							{...register('paidAt')}
						/>

						<TextField
							label="Nota (opcional)"
							size="small"
							multiline
							rows={2}
							placeholder="Ej: pago web proveedor"
							{...register('note')}
						/>
					</>
				)}

				{apiError && <Alert severity="error">{apiError}</Alert>}
			</Box>
		);
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>
				Registrar pago con tarjeta corporativa
				{providerName && (
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontWeight: 400 }}>
						Proveedor: {providerName}
					</Typography>
				)}
			</DialogTitle>
			<DialogContent>{renderContent()}</DialogContent>
			{!duplicate && !cardsLoading && availableCards.length > 0 && (
				<DialogActions>
					<Button onClick={handleClose} disabled={submitting}>
						Cancelar
					</Button>
					<Button type="submit" form="register-payment-form" variant="contained" disabled={submitting}>
						{submitting ? <CircularProgress size={20} /> : 'Registrar pago'}
					</Button>
				</DialogActions>
			)}
			{!cardsLoading && availableCards.length === 0 && (
				<DialogActions>
					<Button onClick={handleClose}>Cerrar</Button>
				</DialogActions>
			)}
		</Dialog>
	);
};
